import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Overlay from 'ol/Overlay';
import { CloseIcon } from '@iconicicons/react';
import { Coordinate } from 'ol/coordinate';
import { useMapContext } from './context/MapContext';

export type MapPopupProps = {
  children: React.ReactNode | React.ReactNode[];
  onClose: () => void;
  closeButton?: boolean;
  autoPan?: boolean;
  fixedPosition?: Coordinate;
  className?: string;
  style?: React.CSSProperties;
};

export type MapPopupRef = {
  show: (position: Coordinate) => void;
  hide: () => void;
  getCoordinates: () => Coordinate | undefined;
};

const _MapPopup = (
  {
    children,
    onClose,
    fixedPosition,
    autoPan = false,
    closeButton = true,
    className = '',
    style = {},
  }: MapPopupProps,
  ref: React.ForwardedRef<MapPopupRef>,
) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const [overlay, setOverlay] = useState<Overlay | null>(null);

  const { map } = useMapContext();

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('keydown', close);
    };
  }, []);

  useEffect(() => {
    const _overlay = new Overlay({
      element: popupRef.current ?? undefined,
      positioning: 'bottom-center',
      stopEvent: true,
      insertFirst: true,
      autoPan: autoPan
        ? {
            animation: {
              duration: 250,
            },
            margin: 50,
          }
        : false,
    });
    setOverlay(_overlay);
    return () => {
      _overlay.dispose();
    };
  }, [autoPan, popupRef]);

  useEffect(() => {
    overlay?.setElement(popupRef.current ?? document.createElement('div'));
  }, [popupRef, overlay]);

  useEffect(() => {
    if (!map || !overlay) return;
    map.addOverlay(overlay);
    return () => {
      map.removeOverlay(overlay);
    };
  }, [map, overlay]);

  useImperativeHandle(
    ref,
    () => ({
      show: (position) => {
        overlay?.setPosition(position);
      },
      hide: () => {
        overlay?.setPosition(undefined);
      },
      getCoordinates: () => {
        return overlay?.getPosition();
      },
    }),
    [popupRef, overlay],
  );

  useEffect(() => {
    if (!map || !overlay) return;
    overlay.setPosition(fixedPosition ?? undefined);
  }, [fixedPosition, overlay, map]);

  return (
    <div className="d-none">
      <div
        ref={popupRef}
        className={`d-flex flex-column bg-light border rounded ${className}`}
        style={{ cursor: 'auto', marginBottom: 16, ...style }}
      >
        {closeButton && (
          <div className="d-block p-2">
            <CloseIcon type="button" aria-label="Close" onClick={onClose} />
          </div>
        )}
        <div className="p-2">{children}</div>
      </div>
    </div>
  );
};

export const MapPopup = forwardRef(_MapPopup);
